import { type ImgHTMLAttributes } from 'react';

import { storageSrcSet } from '@/utils/imageUrl';

/** Shape of a vite-imagetools `?…&as=picture` import. */
export type Picture = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

type BaseProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'alt'> & {
  /** Required. Pass '' for a purely decorative image (§50). */
  alt: string;
  /** The LCP image of a page: load eagerly at high priority (§48). */
  priority?: boolean;
  sizes?: string;
};

export type AppImageProps = BaseProps &
  (
    | { picture: Picture; src?: never }
    | { src: string; picture?: never; width: number; height: number }
  );

/**
 * Every image on the site. Local assets come pre-optimised from the build as
 * AVIF/WebP at several widths; remote (vehicle) images come from Storage.
 * Width and height are always set, so the browser reserves space and the
 * layout never shifts.
 */
export function AppImage({
  picture,
  src,
  alt,
  priority = false,
  sizes = '100vw',
  ...rest
}: AppImageProps) {
  const loading = priority ? 'eager' : 'lazy';
  const fetchPriority = priority ? 'high' : 'auto';

  if (picture) {
    return (
      <picture>
        {Object.entries(picture.sources).map(([format, srcSet]) => (
          <source key={format} type={`image/${format}`} srcSet={srcSet} sizes={sizes} />
        ))}
        <img
          src={picture.img.src}
          width={picture.img.w}
          height={picture.img.h}
          alt={alt}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          {...rest}
        />
      </picture>
    );
  }

  // A photo from Storage is offered at several widths, so a phone does not
  // download a picture sized for a desktop gallery.
  const srcSet = storageSrcSet(src);

  return (
    <img
      src={src}
      {...(srcSet ? { srcSet, sizes } : {})}
      alt={alt}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding="async"
      {...rest}
    />
  );
}
